<template>
    <multiselect
        :id="id"
        v-model="selectedOptions"
        :class="selectClass"
        :label="label"
        track-by="id"
        :placeholder="placeholder"
        open-direction="bottom"
        :options="options"
        :multiple="multiple"
        :searchable="searchable"
        :loading="isLoading"
        :options-limit="perPage"
        :limit="limit"
        :limit-text="limitText"
        :max-height="600"
        :hide-selected="hideSelected"
        :disabled="disabled"
        :select-label="''"
        :selected-label="getI18n('MULTISELECT', 'selected_label')"
        :deselect-label="''"
        :close-on-select="!keepOpenOnSelect"
        :allow-empty="allowEmpty"
        :preselect-first="preselectFirst"
        @search-change="asyncFind"
        @input="handleInput"
    >
        <template slot="option" slot-scope="props">
            <div
                class="multiselect__option d-flex justify-content-between align-items-start"
                :class="{ 'multiselect__option--selected': isItemSelected(props.option) && enableToggle }"
            >
                <span class="multiselect-option-label">{{ getOptionLabel(props.option) }}</span>
                <div class="ml-2">
                    <i
                        v-if="isItemSelected(props.option) && !enableToggle"
                        class="fas fa-check-circle text-success"
                        :title="getI18n('COMMON', 'already_selected') || 'Já selecionado'"
                    ></i>
                    <i
                        v-else-if="isItemSelected(props.option) && enableToggle"
                        class="fas fa-times-circle text-danger multiselect__option-remove"
                        :title="getI18n('COMMON', 'remove') || 'Remover'"
                        @click.stop="handleToggleItem(props.option)"
                    ></i>
                </div>
            </div>
        </template>
        <span slot="noResult">{{
            getI18n('ERROR_CODES', 'search_not_found')
        }}</span>
        <template v-if="totalOptions > perPage" slot="afterList">
            <span class="resultLimit">{{
                getI18n('ERROR_CODES', 'too_many_results')
            }}</span>
        </template>
        <template slot="noOptions">{{
            getI18n('ERROR_CODES', 'empty_selector')
        }}</template>
    </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect'
import BaseService from '@src/services/BaseService'

export default {
    components: {
        Multiselect,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        multiple: {
            type: Boolean,
            required: false,
        },
        placeholder: {
            type: String,
            default: null
        },

        // eslint-disable-next-line vue/require-prop-types
        value: {
            default: null,
        },

        searchable: {
            type: Boolean,
            default: false,
        },

        service: {
            required: true,
            type: String,
        },

        selectClass: {
            type: String,
            default: '',
        },

        optionsToRemoveList: {
            type: Array,
            default: () => [],
        },

        inputFunction: {
            type: Function,
            default: () => ({}),
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        hideSelected: {
            type: Boolean,
            default: false,
        },

        keepOpenOnSelect: {
            type: Boolean,
            default: false,
        },

        label: {
            type: String,
            default: 'name',
        },

        searchLabel: {
            type: String,
            default: null,
        },

        limit: {
            type: Number,
            default: 3,
        },

        limitText: {
            type: Function,
            default: function (count) {
                return `${this.$options.filters.firstToLower(
                    this.getI18n('COMMON.and')
                )} ${count} ${this.$options.filters.firstToLower(
                    this.getI18nModified({
                        prefix: 'COMMON',
                        suffix: 'other_options',
                        modifier: count,
                    })
                )}`
            },
        },

        parameters: {
            type: Object,
            default: () => ({
                per_page: 1000,
                page: 1,
                orderBy: 'name',
                sortedBy: 'asc',
            }),
        },

        defaultOptions: {
            type: Array,
            default: () => [],
        },

        allowEmpty: {
            type: Boolean,
            // eslint-disable-next-line vue/no-boolean-default
            default: true,
        },

        preselectFirst: {
            type: Boolean,
            required: false,
        },
        
        selectedItemsList: {
            type: Array,
            default: () => [],
        },
        
        enableToggle: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            isLoading: false,
            options: this.defaultOptions,
            totalOptions: 0,
            perPage: this.parameters.per_page || 1000,
        }
    },
    computed: {
        selectedOptions: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit('input', value)
            },
        },

        placeholderComputed() {
            return this.placeholder
                ? this.placeholder
                : this.searchable
                    ? this.getI18n('PLACEHOLDERS', 'type_to_search')
                    : this.getI18n('PLACEHOLDERS', 'select_an_option')
        },

        serviceClass: function () {
            return new BaseService(this.service)
        },
    },
    watch: {
        options(value) {
            this.$emit('options', value)
        },
        optionsToRemoveList: {
            deep: true,
            handler() {
                if (this.options.length > 0) {
                    this.removeOptions(this.optionsToRemoveList)
                }
            },
        },
    },
    created() {
        this.loadOptions()
    },
    methods: {
        loadingEvent(loading) {
            this.isLoading = loading
            this.$emit('loading', loading)
        },

        async loadOptions(parameters) {
            this.loadingEvent(true)

            const data = await this.serviceClass
                .fetchAll(parameters ?? this.parameters)
                .then((response) => {
                    this.options = response.data.data

                    if (this.optionsToRemoveList.length > 0) {
                        this.removeOptions(this.optionsToRemoveList)
                    }

                    if (this.preselectFirst && this.options.length > 0 && (!this.selectedOptions || this.selectedOptions.length > 0)) {
                        this.selectedOptions = this.multiple ? [this.options[0]] : this.options[0]
                    }

                    this.loadingEvent(false)
                    this.totalOptions = response.data.meta.total
                    return response.data.data
                })

            return data
        },
        removeOptions(removeOptionsList) {
            this.options = this.options.filter((option) => {
                const index = removeOptionsList.findIndex((find) => {
                    if (typeof find === 'object' && find !== null) {
                        return find.id === option.id
                    }

                    return find === option.id
                })

                return index === -1
            })
        },
        asyncFind(searchQuery) {
            this.loadingEvent(true)
            if (searchQuery.length > 0) {
                const searchLabel = this.searchLabel
                    ? this.searchLabel
                    : this.label
                const searchParameters = {
                    search: `${searchLabel}:${searchQuery}`,
                    searchFields: `${searchLabel}:ilike`,
                    ...this.parameters,
                }
                this.serviceClass
                    .fetchAll(searchParameters)
                    .then((response) => {
                        this.options = response.data.data

                        if (this.optionsToRemoveList.length > 0) {
                            this.removeOptions(this.optionsToRemoveList)
                        }

                        this.totalOptions = response.data.meta.total
                        this.loadingEvent(false)
                    })
            }
            else {
                this.loadOptions()
            }
        },
        clearAll() {
            this.selectedOptions = []
        },
        refresh() {
            this.clearAll()
            this.loadOptions()
        },
        
        getOptionLabel(option) {
            if (!option) return ''
            if (typeof option === 'object' && this.label) {
                return option[this.label] || option.name || option.text || ''
            }
            return String(option)
        },
        isItemSelected(option) {
            if (!this.selectedItemsList || this.selectedItemsList.length === 0 || !option) {
                return false
            }
            const optionId = option && typeof option === 'object' && option.id !== undefined ? option.id : option
            return this.selectedItemsList.some(item => {
                if (!item) return false
                const itemId = item && typeof item === 'object' && item.id !== undefined ? item.id : item
                return String(itemId) === String(optionId)
            })
        },
        handleInput(value) {
            // Se enableToggle está ativo e o item já está selecionado, remover ao invés de adicionar
            if (this.enableToggle && value && this.isItemSelected(value)) {
                this.handleToggleItem(value)
                // Limpar o valor selecionado
                this.$nextTick(() => {
                    this.selectedOptions = null
                })
                return
            }
            
            // Comportamento normal
            this.inputFunction()
        },
        handleToggleItem(option) {
            if (!this.enableToggle || !option) return
            
            const optionId = option && typeof option === 'object' && option.id !== undefined ? option.id : option
            const itemIndex = this.selectedItemsList.findIndex(item => {
                if (!item) return false
                const itemId = item && typeof item === 'object' && item.id !== undefined ? item.id : item
                return String(itemId) === String(optionId)
            })
            
            if (itemIndex > -1) {
                // Emitir evento para remover o item
                this.$emit('remove-item', option)
            }
        },
    },
}
</script>

<style scoped>
.multiselect__option--selected {
    background-color: #f0f8ff;
}

.multiselect-option-label {
    flex: 1;
    min-width: 0;
    white-space: normal;
    word-break: break-word;
}

.multiselect__option-remove {
    cursor: pointer;
    transition: transform 0.2s;
}

.multiselect__option-remove:hover {
    opacity: 0.8;
    transform: scale(1.2);
}
</style>
